import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { HTMLAttributes, ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import {
  DataTable,
  EmptyState,
  HistoryTimeline,
  InlineAlert,
  LicenseBanner,
  LoadingButton,
  QueryStateView,
} from "src/grading/components";
import { DataTableColumn } from "src/grading/components/DataTable/DataTable";
import KeyValueCard, {
  KeyValueRow,
} from "src/grading/components/KeyValueCard/KeyValueCard";
import { usePermissions } from "src/grading/hooks/usePermissions";
import {
  errorCodeCopy,
  errorCodeOf,
  errorMessage,
} from "src/grading/utils/apiError";
import { isDomain, parseDomains } from "src/grading/utils/domains";
import {
  ROSTERING_ACCESS,
  permissions,
} from "src/grading/utils/permissionsHelpers";
import { effectivePrivacySettings } from "src/grading/utils/privacySettings";
import { rosterSourceSummary } from "src/grading/utils/rosterSource";
import {
  DpaDocument,
  LicenseStatus,
  OrganizationView,
  SsoProviderName,
  UpdateDistrictPayload,
} from "src/models/hierarchy";
import { DistrictHistoryCategory, HistoryPageArgs } from "src/models/history";
import { useAppDispatch } from "src/store/hooks";
import { selectLicenseStatus } from "src/store/reducers/auth/authSlice";
import {
  useCreateSchoolMutation,
  useDeleteMfaAttestationMutation,
  useGetDistrictHistoryQuery,
  useGetDistrictQuery,
  useLazyGetDpaDocumentUrlQuery,
  useListSchoolsQuery,
  useSetMfaAttestationMutation,
  useUpdateDistrictMutation,
} from "src/store/reducers/districts/districtsApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";
import DistrictHeader from "./DistrictHeader";
import DpaDialog from "./DpaDialog";
import MfaDialog from "./MfaDialog";
import TimeZoneField from "./TimeZoneField";

const PROVIDERS: SsoProviderName[] = [
  "Google",
  "Microsoft",
  "Clever",
  "ClassLink",
];
const LICENSES: LicenseStatus[] = ["trial", "active", "suspended"];

/** The Overview's history: settings, license, MFA and agreement changes (FR-044, R21). */
const HISTORY_CATEGORIES: DistrictHistoryCategory[] = [
  "district",
  "license",
  "mfa",
  "dpa",
];

const pill = { borderRadius: `${radius.pill}px` } as const;

/**
 * A calendar date (`YYYY-MM-DD`, or the date part of a timestamp) in the viewer's language. It is
 * formatted in UTC so it never shifts a day; null when it can't be read, so nothing shows "Invalid Date".
 */
export const formatCalendarDate = (
  value: string | null | undefined,
  locale: string,
) => {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(date);
};

/** The day of a timestamp in the viewer's time zone and language; null when it can't be read. */
export const formatDay = (value: string | null | undefined, locale: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
};

/**
 * The agreement on file, or null. A legacy placeholder (`version: "unsigned"`, written before R20)
 * or a record without a signer is never shown as signed (FR-038).
 */
export const agreementOnFile = (
  dpa: DpaDocument | null | undefined,
): DpaDocument | null =>
  dpa &&
  dpa.version &&
  dpa.version.toLowerCase() !== "unsigned" &&
  dpa.signedBy &&
  dpa.signedAt
    ? dpa
    : null;

type SettingsField = "name" | "timezone" | "domains" | "provider" | "license";

/** Refusals that belong to one settings field (contracts/district-admin-api.md, PATCH). */
const FIELD_OF_CODE: Record<string, SettingsField> = {
  INVALID_TIMEZONE: "timezone",
  PUBLIC_DOMAIN: "domains",
  DOMAIN_TAKEN: "domains",
};

interface SettingsForm {
  name: string;
  timezone: string | null;
  domains: string;
  provider: SsoProviderName | "";
  license: LicenseStatus;
}

const licenseOf = (district: OrganizationView): LicenseStatus =>
  district.license?.status ?? district.licenseStatus;

const formOf = (district: OrganizationView): SettingsForm => ({
  name: district.name,
  timezone: district.timezone ?? null,
  domains: district.ssoDomains.join(", "),
  provider: district.ssoProvider ?? "",
  license: licenseOf(district),
});

/** Only the fields that differ from the saved district, so the history records real changes. */
const changesOf = (
  form: SettingsForm,
  district: OrganizationView,
  isPlatformAdmin: boolean,
): UpdateDistrictPayload => {
  const body: UpdateDistrictPayload = {};
  const name = form.name.trim();
  if (name !== district.name) body.name = name;
  if (form.timezone !== (district.timezone ?? null))
    body.timezone = form.timezone;
  const domains = parseDomains(form.domains);
  if (domains.join(",") !== district.ssoDomains.join(","))
    body.ssoDomains = domains;
  const provider = form.provider === "" ? null : form.provider;
  if (provider !== (district.ssoProvider ?? null)) body.ssoProvider = provider;
  if (isPlatformAdmin && form.license !== licenseOf(district))
    body.licenseStatus = form.license;
  return body;
};

/** A native select with a stable `data-testid`, like the Rostering tab's. */
const SelectField = ({
  label,
  value,
  testId,
  disabled,
  helperText,
  error,
  onChange,
  children,
}: {
  label: string;
  value: string;
  testId: string;
  disabled?: boolean;
  helperText?: string;
  error?: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) => (
  <TextField
    select
    label={label}
    value={value}
    disabled={disabled}
    error={Boolean(error)}
    helperText={error || helperText}
    onChange={(e) => onChange(e.target.value)}
    SelectProps={{ native: true }}
    // A native select always shows an option, even an empty-valued one, so the label never sits
    // inside the box.
    InputLabelProps={{ shrink: true }}
    inputProps={{ "data-testid": testId }}
  >
    {children}
  </TextField>
);

/** One district's Overview (contracts/console-district-tabs.md, `district`). */
const DistrictPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { districtId } = useParams<{ districtId: string }>();
  const district = useGetDistrictQuery(districtId);

  // District is a navigation section root: no breadcrumb trail.
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  return (
    <QueryStateView query={district} errorMessage={t("district.loadError")}>
      {() =>
        district.data ? (
          <DistrictOverview districtId={districtId} district={district.data} />
        ) : null
      }
    </QueryStateView>
  );
};

const DistrictOverview = ({
  districtId,
  district,
}: {
  districtId: string;
  district: OrganizationView;
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const locale = i18n.language;
  const { hasPermissions } = usePermissions();
  const ownLicense = useSelector(selectLicenseStatus);
  const schools = useListSchoolsQuery(districtId);
  const [updateDistrict, { isLoading: saving }] = useUpdateDistrictMutation();
  const [createSchool, { isLoading: creatingSchool }] =
    useCreateSchoolMutation();
  const [setMfa, { isLoading: recordingMfa }] = useSetMfaAttestationMutation();
  const [deleteMfa, { isLoading: withdrawingMfa }] =
    useDeleteMfaAttestationMutation();
  const [getDocumentUrl, { isFetching: openingDocument }] =
    useLazyGetDpaDocumentUrlQuery();

  const isPlatformAdmin = hasPermissions(
    permissions.CREATE_DISTRICTS.action,
    permissions.CREATE_DISTRICTS.subject,
  );
  // DA for their own district, or PA for any (FR-039); everyone else reads.
  const canEdit =
    isPlatformAdmin ||
    hasPermissions(
      permissions.UPDATE_OWN_DISTRICT.action,
      permissions.UPDATE_OWN_DISTRICT.subject,
    );
  // Read (a suspended license) or manage both open the Rostering tab.
  const canRoster = ROSTERING_ACCESS.some((p) =>
    hasPermissions(p.action, p.subject),
  );
  const canCreateSchool =
    isPlatformAdmin ||
    hasPermissions(
      permissions.CREATE_SCHOOLS.action,
      permissions.CREATE_SCHOOLS.subject,
    );
  const canPrivacy = hasPermissions(
    permissions.MANAGE_PRIVACY.action,
    permissions.MANAGE_PRIVACY.subject,
  );
  const canSeeHistory = canEdit || canPrivacy;

  // Settings form. A background refetch re-seeds it only while it has no unsaved edits.
  const [form, setForm] = useState<SettingsForm>(() => formOf(district));
  const edited = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<SettingsField, string>>
  >({});
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!edited.current) setForm(formOf(district));
  }, [district]);

  const edit = (field: SettingsField, patch: Partial<SettingsForm>) => {
    edited.current = true;
    setForm((f) => ({ ...f, ...patch }));
    setFieldErrors((e) => ({ ...e, [field]: undefined }));
    setSaved(false);
    setSaveError(null);
  };

  const changes = changesOf(form, district, isPlatformAdmin);
  const dirty = Object.keys(changes).length > 0;
  const nameTooShort = form.name.trim().length < 2;

  const save = async () => {
    setSaved(false);
    setSaveError(null);
    const badDomain = parseDomains(form.domains).find((d) => !isDomain(d));
    if (badDomain) {
      setFieldErrors({
        domains: t("district.domainInvalid", { domain: badDomain }),
      });
      return;
    }
    setFieldErrors({});
    try {
      await updateDistrict({ districtId, body: changes }).unwrap();
      edited.current = false;
      setSaved(true);
    } catch (e) {
      const code = errorCodeOf(e);
      const field = code ? FIELD_OF_CODE[code] : undefined;
      const message = errorMessage(t, e, t("district.saveError"));
      if (field) setFieldErrors({ [field]: message });
      else setSaveError(message);
    }
  };

  // Add school, with the duplicate-name confirmation (FR-045).
  const [schoolOpen, setSchoolOpen] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [schoolSisId, setSchoolSisId] = useState("");
  const [duplicateName, setDuplicateName] = useState(false);
  const [sisIdError, setSisIdError] = useState<string | null>(null);
  const [schoolError, setSchoolError] = useState<string | null>(null);

  const closeSchool = () => {
    setSchoolOpen(false);
    setSchoolName("");
    setSchoolSisId("");
    setDuplicateName(false);
    setSisIdError(null);
    setSchoolError(null);
  };

  const addSchool = async (confirmDuplicateName = false) => {
    setSchoolError(null);
    setSisIdError(null);
    try {
      const school = await createSchool({
        districtId,
        body: {
          name: schoolName.trim(),
          externalId: schoolSisId.trim() || undefined,
          ...(confirmDuplicateName ? { confirmDuplicateName: true } : {}),
        },
      }).unwrap();
      closeSchool();
      history.push(`/grading/schools/${school.id}`);
    } catch (e) {
      const code = errorCodeOf(e);
      if (code === "DUPLICATE_NAME") setDuplicateName(true);
      else if (code === "DUPLICATE_SIS_ID")
        setSisIdError(errorCodeCopy(t, code) ?? null);
      else setSchoolError(errorMessage(t, e, t("district.createSchoolError")));
    }
  };

  // Agreements (FR-038 to FR-040).
  const [dpaOpen, setDpaOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [agreementNotice, setAgreementNotice] = useState<string | null>(null);
  const [agreementError, setAgreementError] = useState<string | null>(null);

  const recordMfa = async () => {
    if (!district.ssoProvider) return;
    setMfaError(null);
    try {
      await setMfa({ districtId, provider: district.ssoProvider }).unwrap();
      setMfaOpen(false);
      setAgreementError(null);
      setAgreementNotice(t("district.mfaRecorded"));
    } catch (e) {
      setMfaError(errorMessage(t, e, t("district.mfaSaveError")));
    }
  };

  const withdrawMfa = async () => {
    setAgreementNotice(null);
    setAgreementError(null);
    try {
      await deleteMfa({ districtId }).unwrap();
      setAgreementNotice(t("district.mfaWithdrawn"));
    } catch (e) {
      setAgreementError(errorMessage(t, e, t("district.mfaWithdrawError")));
    }
  };

  const downloadAgreement = async () => {
    setAgreementNotice(null);
    setAgreementError(null);
    try {
      const { url } = await getDocumentUrl(districtId).unwrap();
      if (!url) throw new Error("no document link");
      // Same-tab navigation: a presigned link opens without a popup blocker getting in the way.
      window.location.assign(url);
    } catch (e) {
      setAgreementError(errorMessage(t, e, t("district.dpaDownloadError")));
    }
  };

  const useHistoryPage = (page: Required<HistoryPageArgs>) =>
    useGetDistrictHistoryQuery({
      districtId,
      category: HISTORY_CATEGORIES,
      ...page,
    });

  // FR-035: the vendor with its last sync, or "CSV upload" / "Roster sheet" with the last import.
  const rosterLabel = rosterSourceSummary(t, district.rosterSource, locale);
  const licenseStatus = licenseOf(district);

  const schoolColumns: DataTableColumn<OrganizationView>[] = [
    {
      key: "name",
      header: t("district.school"),
      render: (s) => (
        <Typography component="span" sx={{ fontWeight: 800, fontSize: 14 }}>
          {s.name}
        </Typography>
      ),
    },
    {
      key: "externalId",
      header: t("district.sisId"),
      render: (s) => s.externalId ?? "—",
    },
    {
      key: "timezone",
      header: t("district.timezone"),
      // FR-041: a school without its own zone follows the district's current one.
      render: (s) =>
        s.timezone ??
        (district.timezone
          ? t("district.inheritedTimezone", { zone: district.timezone })
          : "—"),
    },
  ];

  const staffSignIn = [
    district.ssoProvider ??
      (district.ssoDomains.length > 0 ? t("district.passwordSignIn") : null),
    district.ssoDomains.length > 0 ? district.ssoDomains.join(", ") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const mfa = district.mfaAttestation ?? null;
  const dpa = agreementOnFile(district.dpaDocument);
  const canRecordMfa =
    canEdit &&
    Boolean(district.ssoProvider) &&
    (!mfa || mfa.provider !== district.ssoProvider);

  const muted = { display: "block", fontSize: 12.5, color: "text.secondary" };
  const rowActions = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 1,
    mt: 1,
  };

  const licenseSince = formatDay(district.license?.changedAt, locale);
  const signInRows: KeyValueRow[] = [
    {
      label: t("district.license"),
      value: (
        <span data-testid="license-status">
          {t(`district.licenseStatus.${licenseStatus}`)}
          {licenseSince ? (
            <Box component="span" sx={muted}>
              {t("district.licenseSince", { date: licenseSince })}
            </Box>
          ) : null}
        </span>
      ),
    },
    ...(staffSignIn
      ? [{ label: t("district.staffSignIn"), value: staffSignIn }]
      : []),
    {
      label: t("district.mfa"),
      tone: mfa ? ("success" as const) : ("default" as const),
      value: (
        <Box
          component="span"
          data-testid="mfa-status"
          sx={{ display: "block" }}
        >
          {mfa
            ? t("district.mfaAttestedFor", {
                provider: mfa.provider,
                who: mfa.attestedBy,
                date:
                  formatDay(mfa.attestedAt, locale) ??
                  t("district.dateUnknown"),
              })
            : t("district.mfaNone")}
          {mfa?.recordedBy ? (
            <Box component="span" sx={muted}>
              {t("district.recordedBy", { who: mfa.recordedBy })}
            </Box>
          ) : null}
          {canEdit && !district.ssoProvider && !mfa ? (
            <Box component="span" sx={muted}>
              {t("district.mfaNeedsProvider")}
            </Box>
          ) : null}
          {canEdit && (canRecordMfa || mfa) ? (
            <Box component="span" sx={rowActions}>
              {canRecordMfa ? (
                <Button
                  size="small"
                  variant="outlined"
                  data-testid="mfa-record"
                  onClick={() => {
                    setMfaError(null);
                    setMfaOpen(true);
                  }}
                  sx={pill}
                >
                  {t("district.mfaRecord")}
                </Button>
              ) : null}
              {mfa ? (
                <LoadingButton
                  size="small"
                  variant="outlined"
                  color="inherit"
                  data-testid="mfa-withdraw"
                  loading={withdrawingMfa}
                  onClick={() => void withdrawMfa()}
                  label={t("district.mfaWithdraw")}
                  sx={pill}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      label: t("district.dpa"),
      tone: dpa ? ("success" as const) : ("default" as const),
      value: (
        <Box
          component="span"
          data-testid="dpa-status"
          sx={{ display: "block" }}
        >
          {dpa ? (
            <>
              {t("district.dpaOnFile", {
                version: dpa.version,
                signedBy: dpa.signedBy,
                date:
                  formatCalendarDate(dpa.signedAt, locale) ??
                  t("district.dateUnknown"),
              })}
              {dpa.recordedBy ? (
                <Box component="span" sx={muted}>
                  {t("district.recordedByOn", {
                    who: dpa.recordedBy,
                    date:
                      formatDay(dpa.recordedAt, locale) ??
                      t("district.dateUnknown"),
                  })}
                </Box>
              ) : null}
            </>
          ) : (
            t("district.dpaNone")
          )}
          {(dpa?.documentKey || canEdit) && (
            <Box component="span" sx={rowActions}>
              {dpa?.documentKey ? (
                <LoadingButton
                  size="small"
                  variant="outlined"
                  data-testid="dpa-download"
                  loading={openingDocument}
                  onClick={() => void downloadAgreement()}
                  label={t("district.dpaDownload")}
                  sx={pill}
                />
              ) : null}
              {canEdit ? (
                <Button
                  size="small"
                  variant="outlined"
                  data-testid="dpa-record"
                  onClick={() => setDpaOpen(true)}
                  sx={pill}
                >
                  {dpa ? t("district.dpaReplace") : t("district.dpaRecord")}
                </Button>
              ) : null}
            </Box>
          )}
        </Box>
      ),
    },
    {
      label: t("district.rosterSource"),
      value: (
        <>
          {rosterLabel}
          {canRoster && (
            <Box
              component={RouterLink}
              to={`/grading/districts/${districtId}/rostering`}
              aria-label={t("district.openRostering")}
              data-testid="roster-source-link"
              sx={{ color: "primaryInk", fontWeight: 700, ml: 1 }}
            >
              <span aria-hidden="true">→</span>
            </Box>
          )}
        </>
      ),
    },
  ];

  // FR-012: the same effective values as the Privacy tab; defaults fill any key left out.
  const privacy = effectivePrivacySettings(district.privacySettings);
  const privacySummary = [
    privacy.videoAllowed ? t("district.videoOn") : t("district.videoOff"),
    privacy.studentLoginEnabled
      ? t("district.studentSignInOn")
      : t("district.studentSignInOff"),
    privacy.shareDob ? t("district.birthDatesStored") : t("district.ageOnly"),
  ].join(" · ");

  const providerChangeWithdraws =
    Boolean(mfa) && (form.provider || null) !== (district.ssoProvider ?? null);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <DistrictHeader
        districtId={districtId}
        district={district}
        schoolCount={schools.data?.length}
      />

      {/* FR-043: the layout already shows the user's own suspended license. */}
      {ownLicense !== "suspended" ? (
        <LicenseBanner status={licenseStatus} />
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 3,
          alignItems: "start",
          "@media (min-width: 1024px)": {
            gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Paper
            component="section"
            aria-labelledby="district-schools"
            sx={{ borderRadius: `${radius.card}px`, overflow: "hidden" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2.75,
                py: 2,
              }}
            >
              <Typography id="district-schools" variant="h4" component="h2">
                {t("district.schools")}
              </Typography>
              {canCreateSchool && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  data-testid="add-school"
                  onClick={() => setSchoolOpen(true)}
                  sx={{ borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } }}
                >
                  {t("district.addSchool")}
                </Button>
              )}
            </Box>
            {schools.isError ? (
              <Box sx={{ px: 2.75, pb: 2.5 }} data-testid="schools-error">
                <InlineAlert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={schools.isFetching}
                      onClick={() => void schools.refetch()}
                    >
                      {t("common.retry")}
                    </Button>
                  }
                >
                  {t("district.schoolsLoadError")}
                </InlineAlert>
              </Box>
            ) : (
              <DataTable
                aria-label={t("district.schools")}
                columns={schoolColumns}
                rows={schools.data ?? []}
                getRowKey={(s) => s.id}
                getRowData={() => ({ "data-testid": "schools-table-row" })}
                loading={schools.isLoading}
                minWidth={480}
                onRowClick={(s) => history.push(`/grading/schools/${s.id}`)}
                emptyState={
                  <EmptyState
                    headingLevel={3}
                    title={t("district.noSchools")}
                    body={t("district.noSchoolsBody")}
                  />
                }
              />
            )}
          </Paper>

          <Paper
            component="section"
            aria-labelledby="district-settings"
            sx={{
              borderRadius: `${radius.card}px`,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography id="district-settings" variant="h6" component="h2">
              {t("more.settings")}
            </Typography>
            <TextField
              label={t("districts.name")}
              value={form.name}
              onChange={(e) => edit("name", { name: e.target.value })}
              disabled={!canEdit}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name}
              inputProps={{ maxLength: 120, "data-testid": "district-name" }}
            />
            <TimeZoneField
              label={t("district.timezone")}
              value={form.timezone}
              onChange={(timezone) => edit("timezone", { timezone })}
              disabled={!canEdit}
              error={fieldErrors.timezone}
              helperText={t("district.timezoneHelper")}
            />
            <TextField
              label={t("districts.emailDomains")}
              value={form.domains}
              onChange={(e) => edit("domains", { domains: e.target.value })}
              disabled={!canEdit}
              error={Boolean(fieldErrors.domains)}
              helperText={fieldErrors.domains ?? t("district.domainsHelper")}
              inputProps={{ "data-testid": "district-domains" }}
            />
            <SelectField
              label={t("district.identityProvider")}
              value={form.provider}
              testId="district-idp"
              disabled={!canEdit}
              helperText={
                providerChangeWithdraws
                  ? t("district.providerChangeWithdraws")
                  : t("district.identityProviderHelper")
              }
              onChange={(v) =>
                edit("provider", { provider: v as SsoProviderName | "" })
              }
            >
              <option value="">{t("district.passwordOnly")}</option>
              {PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </SelectField>
            {isPlatformAdmin && (
              <SelectField
                label={t("district.license")}
                value={form.license}
                testId="district-license"
                error={fieldErrors.license}
                helperText={t("district.licenseHelper")}
                onChange={(v) =>
                  edit("license", { license: v as LicenseStatus })
                }
              >
                {LICENSES.map((l) => (
                  <option key={l} value={l}>
                    {t(`district.licenseStatus.${l}`)}
                  </option>
                ))}
              </SelectField>
            )}
            {saveError && (
              <InlineAlert severity="error">{saveError}</InlineAlert>
            )}
            {saved && (
              <InlineAlert severity="success">
                {t("district.saved")}
              </InlineAlert>
            )}
            {canEdit && (
              <div>
                <LoadingButton
                  variant="contained"
                  data-testid="district-save"
                  loading={saving}
                  onClick={() => void save()}
                  disabled={!dirty || nameTooShort}
                  label={t("common.save")}
                  sx={pill}
                />
              </div>
            )}
          </Paper>
        </Box>

        <Box
          component="aside"
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <KeyValueCard
            title={t("district.signInAgreements")}
            rows={signInRows}
          />
          {agreementError && (
            <InlineAlert severity="error">{agreementError}</InlineAlert>
          )}
          {agreementNotice && (
            <InlineAlert severity="success">{agreementNotice}</InlineAlert>
          )}
          {canPrivacy && (
            <Box
              component={RouterLink}
              to={`/grading/districts/${districtId}/privacy`}
              data-testid="privacy-card"
              sx={(theme) => ({
                display: "flex",
                flexDirection: "column",
                gap: 1,
                p: "20px 22px",
                borderRadius: `${radius.card}px`,
                bgcolor: theme.palette.primary.light,
                color: theme.palette.text.primary,
                textDecoration: "none",
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.focusRing}`,
                  outlineOffset: 2,
                },
              })}
            >
              <Typography
                variant="overline"
                component="p"
                sx={{ color: "primaryInk", lineHeight: 1.2 }}
              >
                {t("district.privacyDefaults")}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
                {privacySummary}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {t("district.privacyReview")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {canSeeHistory && (
        <Paper
          component="section"
          aria-labelledby="district-history-title"
          data-testid="district-history"
          sx={{ borderRadius: `${radius.card}px`, px: 2.75, py: 2.5 }}
        >
          <Typography
            id="district-history-title"
            component="h2"
            sx={{ fontSize: 18, fontWeight: 800 }}
          >
            {t("district.historyTitle")}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
            {t("district.historyNote")}
          </Typography>
          <HistoryTimeline
            subjectName={district.name}
            usePage={useHistoryPage}
            label={t("district.historyTitle")}
          />
        </Paper>
      )}

      <Dialog
        open={schoolOpen}
        onClose={creatingSchool ? undefined : closeSchool}
        fullWidth
        maxWidth="sm"
        aria-labelledby="new-school-title"
        PaperProps={
          {
            "data-testid": "add-school-dialog",
          } as HTMLAttributes<HTMLDivElement>
        }
      >
        <DialogTitle id="new-school-title" component="h2">
          {t("district.addSchool")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label={t("district.schoolName")}
            value={schoolName}
            onChange={(e) => {
              setSchoolName(e.target.value);
              setDuplicateName(false);
              setSchoolError(null);
            }}
            required
            autoFocus
            inputProps={{ maxLength: 120, "data-testid": "school-name" }}
          />
          <TextField
            label={t("district.schoolExternalId")}
            value={schoolSisId}
            onChange={(e) => {
              setSchoolSisId(e.target.value);
              setSisIdError(null);
              setSchoolError(null);
            }}
            error={Boolean(sisIdError)}
            helperText={sisIdError ?? t("district.schoolExternalIdHelper")}
            inputProps={{ "data-testid": "school-sis-id" }}
          />
          {duplicateName && (
            <InlineAlert
              severity="warning"
              title={t("district.duplicateNameTitle")}
            >
              {errorCodeCopy(t, "DUPLICATE_NAME")}
            </InlineAlert>
          )}
          {schoolError && (
            <InlineAlert severity="error">{schoolError}</InlineAlert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.25 }}>
          <Button
            onClick={closeSchool}
            disabled={creatingSchool}
            color="inherit"
            sx={pill}
          >
            {t("common.cancel")}
          </Button>
          {duplicateName ? (
            <LoadingButton
              variant="contained"
              color="secondary"
              data-testid="confirm-duplicate-name"
              loading={creatingSchool}
              onClick={() => void addSchool(true)}
              label={t("district.addAnyway")}
              sx={pill}
            />
          ) : (
            <LoadingButton
              variant="contained"
              data-testid="create"
              loading={creatingSchool}
              disabled={schoolName.trim().length < 2}
              onClick={() => void addSchool()}
              label={t("common.create")}
              sx={pill}
            />
          )}
        </DialogActions>
      </Dialog>

      <DpaDialog
        open={dpaOpen}
        districtId={districtId}
        replacing={Boolean(dpa)}
        onClose={() => setDpaOpen(false)}
        onSaved={() => {
          setDpaOpen(false);
          setAgreementError(null);
          setAgreementNotice(t("district.dpaRecorded"));
        }}
      />
      {district.ssoProvider ? (
        <MfaDialog
          open={mfaOpen}
          provider={district.ssoProvider}
          loading={recordingMfa}
          error={mfaError}
          onConfirm={() => void recordMfa()}
          onClose={() => setMfaOpen(false)}
        />
      ) : null}
    </Box>
  );
};

export default DistrictPage;
